import React, { PureComponent } from 'react';

import { observable, toJS } from 'mobx';
import { observer } from 'mobx-react';
import cn from 'classnames';

import { Avatar, Button, Input, Popover } from 'antd';
import { EditOutlined, PlusOutlined, UserOutlined } from '@ant-design/icons';

import withNavigateParams from '../../utils/withNavigateParams';
import { NavigateFunction, Outlet } from 'react-router';

import { Sorting } from '../../web-types/Sorting';

import './style.scss';
import MetricChange from '../../controls/MetricChange';
import { admin_fetchEmployees, EmployeeRow } from '../../lib/api-web/employees';
import { TableController } from '../../controls/TableController';
import { autobind } from 'core-decorators';
import AddEmployeeModal from '../../global-modals/AddEmployeeModal';
import QRModal from '../../global-modals/QRModal';
import { admin_fetchPlaces, manager_introducePlaceEmployee, manager_invitePlaceEmployee } from '../../lib/api-web';
import user from '../../stores/user';
import handleError from '../../utils/handleError';
import isValidPhone from '../../utils/isValidPhone';
import PromptModal from '../../global-modals/PromptModal';
import { EntitySelect } from '../../controls/EntitySelect';
import PageTitle from '../../controls/PageTitle';

@observer
class AdminEmployeesPage extends PureComponent<{ navigate: NavigateFunction; params: any; state: any }> {
	@observable contentVisible = false;
	@observable total: number | null = null;
	@observable params: { query: string; byPlace: number | null } = { query: '', byPlace: null };

	@observable employeeInviting = false;

	controller = React.createRef<TableController<{ query: string; byPlace: number | null }, EmployeeRow>>();

	@autobind
	handleTablePropsUpdate(contentVisible: boolean, total: number | null, rowsPerPage: number) {
		this.contentVisible = contentVisible;
		this.total = total;
	}

	@autobind
	async handleLoad(
		params: { query: string; byPlace: number | null },
		offset: number,
		limit: number,
		sorting: Sorting | null,
	) {
		return admin_fetchEmployees(
			{ offset, limit, sorting },
			{ query: params.query, notInGroupId: null, placeId: params.byPlace, chainId: null },
		);
	}

	async handleSimpleAddEmployee() {
		const phoneRole = await AddEmployeeModal.prompt(
			'Enter employee phone number',
			user.user!.places.find(p => p.id === Number(this.props.params.placeId))?.role || null,
			'+971',
			isValidPhone,
			'Please, enter UAE phone number of the employee',
		);
		if (phoneRole === null) {
			return;
		}
		const firstName = await PromptModal.prompt('Enter employee first name', '');
		if (firstName === null) {
			return;
		}
		const lastName = await PromptModal.prompt('Enter employee last name', '');
		if (lastName === null) {
			return;
		}
		this.employeeInviting = true;
		try {
			const response = await manager_introducePlaceEmployee(
				Number(this.props.params.placeId),
				phoneRole.phone,
				firstName,
				lastName,
				phoneRole.role,
			);
			if (response.result) {
				await this.controller.current?.load();
			} else {
				handleError(response.error);
			}
		} catch (err) {
			handleError(err);
		}
		this.employeeInviting = false;
	}

	async handleAddEmployee() {
		const result = await AddEmployeeModal.prompt(
			'Enter employee phone number',
			user.user!.places.find(p => p.id === Number(this.props.params.placeId))?.role || null,
			'+971',
			isValidPhone,
			'Please, enter UAE phone number of employee',
		);
		if (result) {
			const { role, phone } = result;
			this.employeeInviting = true;
			try {
				const response = await manager_invitePlaceEmployee(Number(this.props.params.placeId), phone, role);
				if (response.result) {
					const url = `${document.location.origin}/invite/${response.data}/`;
					await QRModal.prompt('Show this QR code to your employee', url);
				} else {
					handleError(response.error);
				}
			} catch (err) {
				handleError(err);
			}
			this.employeeInviting = false;
		}
	}

	render() {
		if (this.props.params.employeeId) {
			return <Outlet />;
		}

		return (
			<div key="two" className={cn('page', { hidden: !this.contentVisible })}>
				<div className="page-head">
					<PageTitle type="Employees" total={this.total} />
					<div className="page-actions">
						<EntitySelect
							placeholder="Filter by place"
							loader={async (query, id) => {
								const resp = await admin_fetchPlaces(0, 1000, {
									query,
									sorting: { key: 'name', direction: 'ASC' },
								});
								if (!resp.result) {
									return [];
								}
								return resp.data.rows.map(r => ({
									id: r.id,
									title: r.name,
									avatarUrl: r.logoUrl,
								}));
							}}
							selectedId={this.params.byPlace}
							onSelect={id => {
								if (this.params.byPlace !== id) {
									this.params = { ...toJS(this.params), byPlace: id };
								}
							}}
						/>
						<Input.Search
							style={{ marginRight: 15 }}
							type="search"
							onSearch={value => (this.params.query = value)}
							placeholder="Search employee..."
						/>
						<Button
							type="primary"
							icon={<PlusOutlined />}
							onClick={e => {
								e.preventDefault();
								this.handleSimpleAddEmployee();
							}}
						>
							Add
						</Button>
					</div>
				</div>
				<TableController
					ref={this.controller}
					onRowClick={({ id }) => {
						this.props.navigate(`/admin-employees/${id}/`, {
							state: { backable: true },
						});
					}}
					params={this.params}
					load={this.handleLoad}
					onTablePropsUpdate={this.handleTablePropsUpdate}
					columns={[
						{
							key: 'avatarUrl',
							title: '',
							render: value => <Avatar size={50} src={value} icon={<UserOutlined />} />,
							width: 50,
						},
						{
							key: 'firstName',
							title: 'Name',
							sortable: true,
							render: (value, row) => `${row.firstName} ${row.lastName}`,
						},
						{
							key: 'up_text_role',
							title: 'Role',
							sortable: true,
						},
						{
							key: 'rating_7d',
							width: 100,
							titleClass: 'cell-center',
							cellClass: 'cell-center',
							title: `⭐ 7 days`,
							sortable: true,
							render: (value, row) => (
								<MetricChange
									className="table-rating"
									toFixed={2}
									trimZeroz={false}
									newValue={row.rating_7d}
									oldValue={0}
								/>
							),
						},
						{
							key: 'rating_30d',
							width: 100,
							titleClass: 'cell-center',
							cellClass: 'cell-center',
							title: `⭐ 30 days`,
							sortable: true,
							render: (value, row) => (
								<MetricChange
									className="table-rating"
									toFixed={2}
									trimZeroz={false}
									newValue={row.rating_30d}
									oldValue={0}
								/>
							),
						},
						{
							key: 'rating_mnth',
							width: 100,
							titleClass: 'cell-center',
							cellClass: 'cell-center',
							title: `⭐ Month`,
							sortable: true,
							render: (value, row) => (
								<MetricChange
									className="table-rating"
									toFixed={2}
									trimZeroz={false}
									newValue={row.rating_mnth}
									oldValue={0}
								/>
							),
						},
						{
							key: 'rating_week',
							width: 100,
							titleClass: 'cell-center',
							cellClass: 'cell-center',
							title: `⭐ Week`,
							sortable: true,
							render: (value, row) => (
								<MetricChange
									className="table-rating"
									toFixed={2}
									trimZeroz={false}
									newValue={row.rating_week}
									oldValue={0}
								/>
							),
						},
						{
							key: 'id',
							title: 'Actions',
							width: 70,
							render: id => (
								<>
									<Popover content="Edit employee">
										<Button type="text" icon={<EditOutlined />} />
									</Popover>
								</>
							),
						},
					]}
				/>
			</div>
		);
	}
}

export default withNavigateParams(AdminEmployeesPage);
